import { Platform } from '../types/Platform';
import { ConnectedAccountModel, SyncNowResult } from '../types/ConnectedAccountModel';
import { getJson } from './Utils';

// Connected accounts (Api/AccountsController): the signed-in player's platform handles with
// auto-sync and a target group. Every call needs a session; a 401 carries SignIn: true.
const jsonHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

async function fail(response: Response): Promise<never> {
  let message = `${response.status} ${response.statusText}`;
  try {
    const body = await response.json();
    if (body?.Error) message = body.Error;
  } catch {
    /* keep the status line */
  }
  throw new Error(message);
}

async function list(): Promise<ConnectedAccountModel[]> {
  const response = await fetch('/api/Accounts');
  if (!response.ok) return fail(response);
  return (await getJson<ConnectedAccountModel[]>(response)) ?? [];
}

async function connect(
  platform: Platform,
  username: string,
  syncGroupId?: number | null,
): Promise<ConnectedAccountModel> {
  const response = await fetch('/api/Accounts', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ Platform: platform, Username: username, SyncGroupId: syncGroupId ?? null }),
  });
  if (!response.ok) return fail(response);
  return (await response.json()) as ConnectedAccountModel;
}

async function update(
  platform: Platform,
  change: {
    autoSync?: boolean;
    syncGroupId?: number | null;
    clearGroup?: boolean;
    emailNewGames?: boolean;
  },
): Promise<ConnectedAccountModel[]> {
  const response = await fetch(`/api/Accounts/${platform}`, {
    method: 'PUT',
    headers: jsonHeaders,
    body: JSON.stringify({
      AutoSync: change.autoSync ?? null,
      SyncGroupId: change.syncGroupId ?? null,
      ClearGroup: change.clearGroup ?? false,
      EmailNewGames: change.emailNewGames ?? null,
    }),
  });
  if (!response.ok) return fail(response);
  return (await response.json()) as ConnectedAccountModel[];
}

async function disconnect(platform: Platform): Promise<ConnectedAccountModel[]> {
  const response = await fetch(`/api/Accounts/${platform}`, { method: 'DELETE' });
  if (!response.ok) return fail(response);
  return (await response.json()) as ConnectedAccountModel[];
}

async function syncNow(platform: Platform): Promise<SyncNowResult> {
  const response = await fetch(`/api/Accounts/${platform}/sync`, { method: 'POST' });
  if (!response.ok) return fail(response);
  return (await response.json()) as SyncNowResult;
}

export default { list, connect, update, disconnect, syncNow };
